import React, {ReactNode, useCallback} from 'react';
import './TransactionCard.less';
import '../../stylesheets/form.less';
import Transaction from "../../entities/transaction";
import util from "../../lib/util";
import OkCancel from "../../ui/Buttons/OkCancel";
import FormElement from "../../lib/FormElement";
import Matter from "../../entities/matter";
import CaseHelper from "../../Case/CaseHelper";
import IconButton from "../../ui/IconButton/IconButton";
import Contact from "../../entities/contact";
import LookupRegistry from "../../ui/LookupSelect/LookupRegistry";
import FinancialHelper from "../FinancialHelper";
import * as DateUtil from "../../lib/dateutils";
import ContactSelect, {ContactSelectHandle} from '../../Contacts/ContactSelect/ContactSelect';
import failureLadder from '../../lib/failureLadder';
import i18n from '../../i18n/i18n';
import HelpMessage from '../../i18n/HelpMessage';
import {formatFMLFromId, formatLFM, sortContacts} from "../../Contacts/ContactFormat";
import {isPortalUser} from "../../lib/picos/currentLogin";
import Icons from "../../ui/IconButton/Icons";
import {getParticipants} from "../../lib/picos/participantHelper";
import type {RootState} from "../../@types/store";
import type {SelectOption} from "../../@types/select";
import {uuid} from "../../@types/entity";
import {TransactionChildCardProps} from "./TransactionCard";
import {useRootSelector} from "../../hooks/useOutlawStore";

type AccountData = {
    id: uuid,
    type: string,
    name: ReactNode,
    balance: number,
    contact?: Contact
};

/**
 * See documentation for the Transaction schema (schemas/transaction.js) for a full rundown on how the Transaction works, and which
 * bits are used for each type of transaction.
 *
 * This card can be called from either an EditableTable or directly from a button press.  The env and startEditing props are defaulted
 * so that coming in from outside of an EditableTable still works.
 *
 *
 *
 * There are two ways that this card is shown: from an EditableTable of transactions in Financials, and directly from a Matter or Contact.
 * Check props.env.matter and props.env.contact to see if either of these cases are true.
 *
 * If we are called from a Matter, we will assume that the matter is unchangeable, but the contact (if > 1) is.
 * If we are called from a Contact, we will assume that the contact is unchangeable, but the Matter is.
 *
 * For a payment, we show a contact first, then list their cases. Click on the cases that we will apply the funds to
 */
function WithdrawalCard(props: WithdrawalCardProps) {
    let recipientRef = React.createRef<ContactSelectHandle>();
    
    // OWA-552: guarantee a non-null `current` in code, not via a prop-type contract — this
    // card never depends on the parent always passing one. props.current is present in practice.
    if (!props.current && import.meta.env.DEV) console.warn('<WithdrawalCard/> rendered without a current Transaction; using a blank fallback.');
    const {current = new Transaction({}), matter, onCancel, onChange, onOk} = props;

    const contacts = useRootSelector(state => state.contacts);
    const leads = useRootSelector(state => state.leads);
    const matters = useRootSelector(state => state.matters);

    /**
     * Get trust accounts for the clients and matters.
     * There will be one trust per matter, and one trust for each client in common to all matters.
     *
     * However, if this is a payment, and there are multiple matters selected, there are three options:
     * 1. Put each distribution into the related client trust
     * 2. Put the whole amount into the client trust.
     * 3. Put the whole amount into
     *
     * Payments: trusts are used as a target (payment is always an external entity).
     *
     * Need to look at the 'field' parameter (to/from) to make sure we are not allowing the same to/from funding source.
     *
     * @param {string[]} types
     * @param {'to'|'from'} [field]
     * @return {Array.<{label: string, value: uuid, data: object}>}
     */
    const getTrustAccounts = useCallback((types: string[], field = 'from'): SelectOption<string, AccountData>[] => {
        // Get matters specific to the case
        let _matters = matter
                      ? [matter]
                      : current.matterIds.map(mid => CaseHelper.getMatterFromId(mid));

        // If not clients in common, we can not pull from any client trusts.
        const clientTrusts: AccountData[] = types.includes('G')
                                            ? current.clientIds
                                                .map(cid => {
                                                    // Get the balance for this trust
                                                    const contact = contacts.get(cid);
                                                    const clientName = formatLFM(contact, true);
                                                    const trustBalance = FinancialHelper.getTrustAmount('G', cid, {asOf: current.txDate});

                                                    return {
                                                        id: cid,
                                                        type: 'G',
                                                        name: FinancialHelper.formatLabel(Icons.USER, i18n.cvs('trustFor', {value: clientName}), trustBalance, 'blue'),
                                                        balance: trustBalance,
                                                        contact
                                                    };
                                                })
                                            : [];

        // Add the trust for the matter
        const matterTrusts: AccountData[] = types.includes('M') ? _matters
            .filter(m => field === 'from'
                         ? !current.dbAddedOn
                           ? FinancialHelper.getTrustAmount('M', m.id) > 0
                           : true
                         : true)
            .map(matter => {
                const trustAmount = FinancialHelper.getTrustAmount('M', matter.id);

                return {
                    id: matter.id,
                    type: 'M',
                    name: FinancialHelper.formatLabel(Icons.CASE, i18n.cvs('trustFor', {value: matter.name}), trustAmount, 'brown'),
                    balance: trustAmount,
                    data: matter
                };
            }) : [];

        const merged = matter ? [...matterTrusts, ...clientTrusts] : [...clientTrusts, ...matterTrusts];

        return merged.map(account => {
            return {
                label: account.name as string,
                value: account.id,
                data: account
            };
        });
    }, [contacts, current.clientIds, current.dbAddedOn, current.matterIds, current.txDate, matter]);

    /**
     * A disbursement in this context has two forms:
     * 1. Funds from the client are used to pay a bill (client or matter trusts plus Escrow accounts),
     * 2. Money in the trust is being returned to the client (client or matter trusts plus Escrow accounts).
     *
     * The disbursement can be made to a specific person or company (Contact), NOT to another account!
     * That would be a transfer, not a disbursement.  So with a disbursement, the target will be a ContactId,
     * the targetType will be 'C', and the amount will be removed from the source account.
     *
     * List trust accounts for each client and matter, along with Escrow accounts.
     *
     * @param {Transaction} _tr
     * @param {'to'|'from'} [field]
     * @return {object[]} - formatted objects with label, value and data
     */
    const getWithdrawalAccounts = useCallback((_tr: Transaction, field = 'from') => {
        return getTrustAccounts(['G', 'M'], field);
    }, [getTrustAccounts]);

    /**
     * Get a list of bank accounts and trust accounts.  If client(s) or a matter are selected, we can show the trust for each of them.
     * @param {Transaction} tr - the current transaction
     * @return {object[]} - formatted objects with label, value and data
     */
    const getSourceAccounts = useCallback((tr: Transaction) => {
        const accounts = getWithdrawalAccounts(tr);

        if (tr) {
            return accounts.filter(at => at.data.id === tr.source);
        }
        else {
            return accounts;
        }
    }, [getWithdrawalAccounts]);

    /**
     * Get all contacts in the system, used for source type of 'C'.  Put the clients for this transaction at the top,
     * along with Benefactors.  This is only used to populate sources, not targets.
     * of the list.
     *
     * @param {Transaction} [tr]
     */
    const getPayees = useCallback((tr?: Transaction) => {
        const tx = current;

        // Get the clients related to this client/matter
        const clients = tx.clientIds || [];

        const _matters = (tx.matterIds.length === 0
                         ? CaseHelper.getMattersForClients(tx.clientIds, true)
                         : tx.matterIds.map(mid => CaseHelper.getMatterFromId(mid))).filter(util.existsFilter);

        // We allow withdrawals from leads
        const leadPayers = tx.txType === 'RR'
                           ? util.flatten(leads
                .filter(l => util.arrayIntersection(l.clients, clients).length > 0)
                .map(l => l.payers))
                           : [];

        // Find clients for matters
        const matterClients = _matters
            .map(m => m.clients)
            .flat();

        // Get benefactors for the cases
        const benefactors = _matters
            .map(m => getParticipants(m.id))
            .flat()
            .filter(p => p.relationships?.includes(LookupRegistry.SACROSANCT.BENEFACTOR))
            .map(p => p.contactId);

        // This is a list of targeted contacts to put at the top of the list
        const targetIds = clients.concat(benefactors)
            .concat(leadPayers)
            .concat(matterClients)
            .filter(util.distinctFilter);

        const results = contacts
            .sort((a, b) => targetIds.includes(a.id)
                            ? -1
                            : targetIds.includes(b.id)
                              ? 1
                              : formatLFM(a).localeCompare(formatLFM(b))
            )
            .map(c => {
                if (c.status === 'D') return null;

                const name = formatLFM(c);
                const label = clients.includes(c.id)
                              ? FinancialHelper.formatLabel(Icons.CLIENT, name, null, 'navy')
                              : benefactors.concat(leadPayers).includes(c.id)
                                ? FinancialHelper.formatLabel(Icons.MONEY_IN, name, null, 'green')
                                : FinancialHelper.formatLabel(c.isCompany ? Icons.BUSINESS : Icons.CONTACT, name, null, 'gray');

                return {
                    label,
                    contactName: name,
                    value: `${c.id}`,
                    contact: c,
                    data: c,
                };
            })
            .filter(util.existsFilter);

        if (tr) return results.filter(v => v.data.id === tr.target);
        else return results;
    }, [contacts, current, leads]);

    /**
     * Get a list of target accounts.
     *
     * If a transaction is passed, just return the object matching that transaction's target
     *
     * @param {Transaction} [tr]
     */
    const getTargetAccounts = useCallback((tr: Transaction) => {
        const accounts = getPayees(tr);

        if (tr) {
            return accounts.filter(at => at.data.id === tr.target);
        }
        else {
            return accounts;
        }
    }, [getPayees]);

    /**
     * Get a list of matters.
     *
     * For TT, only show clients that have funds in trust (matter or general).
     *
     * @param {Transaction} tr
     * @return {object[]} - formatted objects with label, value and data
     */
    const getClients = useCallback((tr: Transaction) => {
        // Get a unique list of all clients
        const matterClients = (CaseHelper.getClients(true) as uuid[])
            .filter(cid => tr ? FinancialHelper.getTrustAmount('A', cid) > 0 : true);

        const leadClients = util.flatten(leads.map(l => l.clients)) as uuid[];

        const getIcon = (c: Contact) => {
            if (matterClients.includes(c.id)) return Icons.CASE;
            else return Icons.LEAD;
        };

        const getColor = (c: Contact) => {
            if (matterClients.includes(c.id)) return '#b3953c';
            else return 'green';
        };

        const clients = matterClients.concat(leadClients)
            .filter(util.distinctFilter)
            .filter(util.existsFilter)
            .map(cid => contacts.get(cid))
            .sort(sortContacts)
            .map(c => {
                const name = formatLFM(c);
                return {
                    label: <IconButton type={getIcon(c)} color={getColor(c)} label={name}/>,
                    value: name,
                    data: c
                };
            });

        // Filter based on state, rather than the transaction
        if (tr) {
            // Only return entries for the clients in state
            return clients.filter(c => tr.clientIds.includes(c.data.id));
        }
        else return clients;
    }, [leads, contacts]);

    /**
     * Get a list of matters.  Only show matters where the selected clients are available.
     *
     * @param {Transaction} tr
     */
    const getMatters = useCallback((tr: Transaction): SelectOption<string, Matter>[] => {
        // Find all matters related to these clients.
        const _matters = matters
            // Matter must include every client in clientIds
            .filter(m => current.clientIds.every(cid => m.clients.includes(cid)))
            .map(m => {
                return {label: m.name, value: m.name, data: m};
            });

        if (tr) {
            return _matters.filter(m => tr.matterIds.includes(m.data.id));
        }
        else return _matters;
    }, [current.clientIds, matters]);

    /**
     *
     * @param {Array.<{label: string, value: string, data: Contact}>} values
     */
    const onClientsChange = useCallback((values: SelectOption<string, Contact>[]) => {
        const clone = current.clone();

        // We always set the clientIds field
        clone.clientIds = values.map(v => v.data.id);

        onChange(clone);
    }, [current, onChange]);

    /**
     * Right now this only selects one value, because distributions is not capable of handling
     * multiple matters, each with its own source... when that is ready, switch over to the version
     * below.
     */
    const onMattersChange = useCallback((values: SelectOption<string, Matter>) => {
        const clone = current.clone();
        clone.matterIds = values ? [values.data.id] : [];

        onChange(clone);
    }, [current, onChange]);

    const onSourceChange = useCallback((value: SelectOption<string, any>) => {
        const clone = current.clone();
        clone.source = value.data.id;
        clone.sourceType = value.data.type;

        // Get the total in this matter/client trust, and make that the default amount for the transaction
        if (clone.txType === 'RR') clone.amount = FinancialHelper.getTrustAmount(clone.sourceType, clone.source);

        onChange(clone);
    }, [current, onChange]);

    /**
     * Change to a recipient
     *
     * @param {uuid} contactId
     */
    const onRecipientChange = useCallback((contactId: uuid) => {
        const clone = current.clone();
        clone.target = contactId;
        clone.targetType = 'C';

        onChange(clone);
    }, [current, onChange]);

    /**
     * @param {Date} dt
     */
    const onVoidedDate = useCallback((dt: Date) => {
        const clone = current.clone();
        clone.voidedDate = dt;

        onChange(clone);
    }, [current, onChange]);

    /**
     * Voided checkbox clicked.
     */
    const onVoidedCheck = useCallback(() => {
        const clone = current.clone();
        clone.voidedDate = clone.voidedDate ? null : new Date();

        onChange(clone);
    }, [current, onChange]);

    /**
     * @param {FormElement} element
     * @return {JSX.Element}
     */
    const renderClientName = useCallback((element: FormElement) => {
        let label = i18n.c('client', true);
        let value: ReactNode;

        if (props.client) {
            value = formatLFM(props.client, true);
        }
        else if (matter) {
            label = i18n.c('clients', true);

            value = matter.clients
                .map(cid => formatFMLFromId(cid, false))
                .join(', ');
        }
        else {
            const clients = current.clientIds.map(cid => formatFMLFromId(cid));
            if (clients.length > 1) label = i18n.c('clients', true);
            value = clients.join(', ');
        }

        return element.renderElement('clientName', label, <div>{value}</div>, true);
    }, [current.clientIds, matter, props.client]);

    const onTransactionDateChanged = useCallback((dt: Date) => {
        const clone = current.clone();
        clone.txDate = dt;

        onChange(clone);
    }, [current, onChange]);

    /**
     * @param {FormElement} element
     */
    const renderMatterName = useCallback((element: FormElement) => {
        const name = matter?.name || current.matterIds.map(mid => CaseHelper.getMatterFromId(mid)?.name).join(', ');
        return element.renderElement('matterName', 'Case', <div>{name}</div>, false);
    }, [current.matterIds, matter?.name]);

    /**
     * Helper function for brevity.
     * @param {string} key
     * @param {object} [params]
     * @returns {JSX.Element|string}
     */
    const l = (key: string, params?: any): string => i18n.ls('transaction', key, params);

    const element = props.element;
    const tr = current;
    const readOnly = props.readOnly || tr.invoiceId;

    // We can now determine if we are clear to save.
    const disabled = readOnly ? false : !tr.source || !tr.target || !tr.isValid();

    const disabledMessage = readOnly ? undefined : failureLadder([
        [!tr.txDate, l('need', {v: 'date'})],
        [tr.clientIds.length === 0, l('need', {v: 'client'})],
        [!tr.source, l('need', {v: 'trust'})],
        [tr.amount === 0, l('need', {v: 'amount', an: true})],
        [!tr.target, l('need', {v: 'payee'})],
        [!tr.isValid(), tr.validationError],
    ]);

    if (props.readOnly || tr.invoiceId) return (
        <>
            {/* Transaction Date */}
            {element.renderElement('txDate',
                <HelpMessage module='transaction' field='date'/>,
                DateUtil.formatDate(tr.txDate))}

            {/* Client(s) */}
            {renderClientName(element)}

            {/* Matter(s) */}
            {matter && renderMatterName(element)}
            {!matter && (tr.clientIds.length > 0) && renderMatterName(element)}

            {/* Source */}
            {element.renderElement('source', <HelpMessage module='transaction' field='wFromTrust'/>,
                tr.txType === 'RO' ? 'Operating' : getSourceAccounts(tr).map((o, i) => <div
                    key={i}>{o.label}</div>)
            )}

            {/* Amount */}
            {element.renderElement('amount', 'Amount', util.formatCurrency(tr.amount))}

            {/* Target */}
            {element.renderElement('target',
                <HelpMessage field='wPayee' module='transaction'/>,
                getTargetAccounts(tr).map((o, i) => <div key={i}>{o.label}</div>))
            }

            {/* Voided Date */}
            {tr.voidedDate && element.renderElement('voidedDate',
                <HelpMessage field='wVoidedOn' module='transaction'/>,
                DateUtil.formatDate(tr.voidedDate))
            }

            {/* Notes */}
            {isPortalUser() ? element.renderElement('notes',
                                <HelpMessage module='transaction' field='notes'/>,
                                <div>{tr.notes}</div>
                            )
                            : element.renderMultilineTextOnly('notes',
                    <HelpMessage module='transaction' field='notes'/>,
                    false)}

            {/* Button bar */}
            {(onOk && onCancel) &&
				<OkCancel label={i18n.ls('transaction', 'title')}
				          disabled={!!disabled}
				          disabledMessage={disabledMessage}
				          useDisabledMessage={true}
				          onCancel={onCancel}
				          onSubmit={onOk}
				          onTop={true}
                    // likesEntityType='transaction'
                    // likesEntityId={tr.id}
				/>
            }
        </>
    );
    else return (
        <>
            {/* Transaction Date */}
            {element.renderDateTime('txDate',
                <HelpMessage module='transaction' field='date'/>,
                onTransactionDateChanged, false, true)}

            {/* Client(s)
                 *
                 * -- Always show! --
                 *
                 * Allow multiple clients ONLY if they both exist within the same case.  That means we will need to
                 * filter available contacts based on clients in cases.
                 *
                 * If we are passed a client or matter, that means we are within the Contacts or Cases page,
                 * and we need to be restricted to those entities.
                 */}
            {(props.client || matter)
             ? renderClientName(element) // Just render the client name, no choices
             : element.renderSelect('clients',
                    <HelpMessage field='clients' module='transaction'/>,
                    true, false, getClients, onClientsChange, true,
                    {disablePortaling: props.portaled})
            }

            {/* Matter(s) */}
            {matter && renderMatterName(element)}
            {(tr.txType !== 'RO') && !matter && (tr.clientIds.length > 0) &&
                element.renderSelect('matterIds',
                    <HelpMessage module='transaction' field='wCase'/>,
                    false, true, getMatters, onMattersChange, true,
                    {
                        disablePortaling: props.portaled,
                        placeholder: l('leaveEmpty')
                    })
            }

            {/* Source */}
            {tr.txType === 'RO'
             ? element.renderElement('source', i18n.l('transaction', 'fromAccount'), 'Operating')
             : element.renderSelect('source',
                    <HelpMessage module='transaction' field='wFromTrust'/>,
                    false, false, getSourceAccounts, onSourceChange, true,
                    {disablePortaling: props.portaled}
                )}

            {/* Amount - all funds in (matter + possibly client?) trust for RR, any amount < trust amount for DT */}
            {element.renderInput('amount', 'Amount', 'money', true)}

            {/* Target - The client/benefactor for RR or any OTHER contact for DT */}
            {element.renderElement(
                'target',
                <HelpMessage field='wPayee' module='transaction'/>,
                <ContactSelect ref={recipientRef}
                               current={tr.target}
                               isMulti={false}
                               name='PaymentCardPayee'
                               parentId={tr.id}
                               onChange={onRecipientChange}
                               autoSave={true}
                               disablePortaling={props.portaled}
                               options={getPayees()}
                />, true)}

            {/* Voided Date */}
            {element.renderDateWithCheckbox('voidedDate',
                <HelpMessage field='wVoidedOn' module='transaction'/>,
                false, onVoidedCheck, onVoidedDate)
            }

            {/* Notes */}
            {element.renderMultilineTextOnly('notes',
                <HelpMessage field='notes' module='transaction'/>,
                false
            )}

            {/* Button bar */}
            {(onOk && onCancel) &&
				<OkCancel label={i18n.ls('transaction', 'title')}
				          disabled={!!disabled}
				          disabledMessage={disabledMessage}
				          useDisabledMessage={true}
				          onCancel={onCancel}
				          onSubmit={onOk}
				          onTop={true}
                    // likesEntityType='transaction'
                    // likesEntityId={tr.id}
				/>
            }
        </>
    );
}

export type WithdrawalCardProps = TransactionChildCardProps & Pick<RootState, 'contacts' | 'leads' | 'matters'>;

export default WithdrawalCard;
